import { useState, useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Menu, X } from "lucide-react";

const IkoMark = () => (
  <div className="flex items-center gap-3" data-testid="logo-mark">
    <div className="relative w-8 h-8 rounded-2xl bg-[#0A0810] flex items-center justify-center overflow-hidden">
      <img
        src="https://customer-assets.emergentagent.com/job_lumirun-agency/artifacts/z78wj5mo_LinkedIn-400px-logo-L-blanc-fond-noir.png"
        alt="Lumiron"
        className="w-full h-full object-contain"
      />
    </div>
    <span className="font-display text-[22px] font-semibold tracking-tighter text-[#1A1814] lowercase">
      iko
    </span>
  </div>
);

export default function Header() {
  const { locale, setLocale, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "/#how", label: t("nav.how") },
    { href: "/#usecases", label: t("nav.cases") },
    { href: "/#security", label: t("nav.security") },
  ];

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "glass border-b border-[#ECE8DD]/50"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container-iko flex items-center justify-between h-[76px]">
        <a href="/#top" className="flex items-center pr-6" data-testid="logo-home">
          <IkoMark />
        </a>

        <nav
          className="hidden lg:flex items-center gap-10 absolute left-1/2 -translate-x-1/2"
          data-testid="primary-nav"
        >
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-[13px] font-medium text-[#6B6862] hover:text-[#1A1814] transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          {/* FR/EN — discreet */}
          <div
            className="hidden sm:flex items-center gap-1 font-mono text-[10.5px] font-medium tracking-[0.12em]"
            data-testid="language-toggle"
          >
            <button
              onClick={() => setLocale("fr")}
              data-testid="lang-fr"
              className={`px-1 py-0.5 transition-colors ${
                locale === "fr"
                  ? "text-[#1A1814]"
                  : "text-[#94918A] hover:text-[#1A1814]"
              }`}
            >
              FR
            </button>
            <span className="text-[#94918A]/40">/</span>
            <button
              onClick={() => setLocale("en")}
              data-testid="lang-en"
              className={`px-1 py-0.5 transition-colors ${
                locale === "en"
                  ? "text-[#1A1814]"
                  : "text-[#94918A] hover:text-[#1A1814]"
              }`}
            >
              EN
            </button>
          </div>

          <a
            href="/#waitlist"
            data-testid="header-cta"
            className="hidden md:inline-flex items-center px-5 py-2.5 rounded-full bg-[#1A1814] text-white text-[13px] font-medium hover:bg-[#437DEA] transition-colors"
          >
            {t("nav.cta")}
          </a>

          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 -mr-2 text-[#1A1814]"
            aria-label="menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#ECE8DD] glass" data-testid="mobile-nav">
          <div className="container-iko py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-base font-medium text-[#1A1814]"
              >
                {l.label}
              </a>
            ))}
            <div className="flex items-center gap-2 pt-2 font-mono text-[11px] tracking-[0.12em]">
              <button
                onClick={() => setLocale("fr")}
                className={locale === "fr" ? "text-[#1A1814] font-semibold" : "text-[#94918A]"}
              >
                FR
              </button>
              <span className="text-[#94918A]/40">/</span>
              <button
                onClick={() => setLocale("en")}
                className={locale === "en" ? "text-[#1A1814] font-semibold" : "text-[#94918A]"}
              >
                EN
              </button>
            </div>
            <a
              href="/#waitlist"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center px-5 py-3 rounded-full bg-[#1A1814] text-white text-sm font-medium"
            >
              {t("nav.cta")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
